export type FetchRequestFailureKind = 'network' | 'aborted'

type FetchRequestDetails = {
  url?: string
  method?: string
}

export class FetchRequestError extends Error {
  readonly name = 'FetchRequestError'

  readonly code = 'FETCH_REQUEST_ERROR'

  readonly kind: FetchRequestFailureKind

  readonly request: FetchRequestDetails

  readonly isOnline: boolean

  readonly originalMessage?: string

  override readonly cause?: unknown

  constructor({
    kind,
    message,
    request,
    cause
  }: {
    kind: FetchRequestFailureKind
    message: string
    request: FetchRequestDetails
    cause?: unknown
  }) {
    super(message)
    this.kind = kind
    this.request = request
    this.isOnline = navigator.onLine
    this.originalMessage = cause instanceof Error ? cause.message : undefined
    this.cause = cause
  }
}

const getRequestUrl = (input: RequestInfo | URL) => {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.toString()
  if ('url' in input) return input.url
  return undefined
}

const getRequestMethod = (input: RequestInfo | URL, init?: RequestInit) => {
  if (init?.method) return init.method
  if (typeof input === 'object' && !(input instanceof URL) && 'method' in input)
    return input.method
  return undefined
}

export const isFetchRequestError = (error: unknown): error is FetchRequestError =>
  error instanceof FetchRequestError

export const isFailedToFetchLikeError = (error: unknown) => {
  if (!(error instanceof Error)) return false

  const normalizedMessage = error.message.trim().toLowerCase()
  const failedToFetchMessages = [
    'failed to fetch',
    'load failed',
    'networkerror when attempting to fetch resource'
  ]

  return (
    (error.name === 'TypeError' && failedToFetchMessages.includes(normalizedMessage)) ||
    normalizedMessage.includes('failed to fetch')
  )
}

export const shouldFilterSentryFetchError = (error: unknown) => {
  if (isFetchRequestError(error)) return error.kind === 'network' || error.kind === 'aborted'
  return isFailedToFetchLikeError(error)
}

export const classifyFetchError = (
  error: unknown,
  input: RequestInfo | URL,
  init?: RequestInit
) => {
  if (isFetchRequestError(error)) return error

  if (error instanceof DOMException && error.name === 'AbortError') {
    return new FetchRequestError({
      kind: 'aborted',
      message: 'Request was interrupted before completion.',
      request: {
        url: getRequestUrl(input),
        method: getRequestMethod(input, init)
      },
      cause: error
    })
  }

  if (isFailedToFetchLikeError(error)) {
    return new FetchRequestError({
      kind: 'network',
      message: 'Unable to reach the server.',
      request: {
        url: getRequestUrl(input),
        method: getRequestMethod(input, init)
      },
      cause: error
    })
  }

  if (error instanceof Error) return error
  return new Error('Unknown request error')
}
